import { BidDetailFragment, useGetBidsByOfferingQuery } from 'graphql/bindings/AdminBindings'
import {
  Card,
  CardHeader,
  CardTable,
  CardTableDataCol,
  CardTableRow,
  CheckIcon,
  CloseIcon,
  Typography,
  useRouter,
  useTheme,
} from '@forgeglobal/design-framework'
import { Routes } from 'util/Routes'
import { formatMoney } from 'util/ParsersFormatters'
import { getHumanReadableSource } from 'util/AccountUtils'
import React, { useEffect, useState } from 'react'
import moment from 'moment'
import orderBy from 'lodash/orderBy'

import { LoadingRow } from './CampaignParticipantsDetail'

interface CampaignBidsTableProps {
  offeringId: string
}

const bidAttributeMap = {
  name: (bid: BidDetailFragment) => bid.order?.entity?.crm_account?.name.toLowerCase(),
  bidAmount: (bid: BidDetailFragment) => bid.dollar_amount,
  status: (bid: BidDetailFragment) => bid.status,
  isNCFSigned: (bid: BidDetailFragment) => bid.order?.entity?.new_client_form_signed.toString(),
  isOrderDocsSigned: (bid: BidDetailFragment) => bid?.order?.client_signed.toString(),
  isWireInfoSent: (bid: BidDetailFragment) => bid?.order?.wire_info_sent.toString(),
  dateCreated: (bid: BidDetailFragment) => bid?.order?.date_created,
  source: (bid: BidDetailFragment) => bid.opportunity?.source,
}

const filterBids = (
  bids: BidDetailFragment[],
  bidAttributeMap: { [key: string]: (bid: BidDetailFragment) => string | undefined },
  columnName: string,
  direction: 'asc' | 'desc'
) => {
  return orderBy(bids, bidAttributeMap[columnName], [direction])
}

const columnDefs = [
  {
    name: 'name',
    label: 'Account name',
    minWidth: 100,
    isLabelHidden: false,
    isSortable: true,
    priority: 1,
  },
  {
    name: 'bidAmount',
    label: 'Bid Amount',
    minWidth: 100,
    isLabelHidden: false,
    isSortable: true,
    priority: 2,
  },
  {
    name: 'status',
    label: 'Payment',
    minWidth: 125,
    isLabelHidden: false,
    isSortable: true,
    priority: 3,
  },
  {
    name: 'isNCFSigned',
    label: 'NCF?',
    minWidth: 50,
    isLabelHidden: false,
    isSortable: true,
    priority: 4,
  },
  {
    name: 'isOrderDocsSigned',
    label: 'Order docs?',
    minWidth: 50,
    isSortable: true,
    isLabelHidden: false,
    priority: 5,
  },
  {
    name: 'isWireInfoSent',
    label: 'Wire info sent?',
    minWidth: 100,
    isSortable: true,
    isLabelHidden: false,
    priority: 6,
  },
  {
    name: 'dateCreated',
    label: 'Date bid created',
    minWidth: 150,
    isSortable: true,
    isLabelHidden: false,
    priority: 7,
  },
  {
    name: 'source',
    label: 'Bid source',
    minWidth: 150,
    isSortable: true,
    isLabelHidden: false,
    priority: 7,
  },
]

export const CampaignBidsTable: React.FC<CampaignBidsTableProps> = ({ offeringId }) => {
  const theme = useTheme()
  const { history } = useRouter()
  const defaultTableVars = {
    id: offeringId,
  }
  const { data, loading } = useGetBidsByOfferingQuery({
    variables: {
      ...defaultTableVars,
    },
  })

  const bids = data?.offering.bids ?? []
  const [sortedBids, setSortedBids] = useState(bids)

  useEffect(() => {
    setSortedBids(bids)
  }, [bids])

  const handleSort = (columnName: string, direction: 'asc' | 'desc' | undefined) => {
    if (direction) {
      const orderedBids = filterBids(bids, bidAttributeMap, columnName, direction)
      setSortedBids(orderedBids)
    }
  }

  return (
    <CardTable colDefs={columnDefs} onSort={handleSort}>
      {sortedBids.map((bid: BidDetailFragment) => {
        const { id, dollar_amount, order, status, opportunity } = bid

        const columns = {
          name: (
            <CardTableDataCol name="name">
              <Typography
                variant="subtitle2"
                onClick={() => history.push(Routes.account(order?.entity?.crm_account?.id))}>
                {order?.entity?.crm_account?.name ?? 'Name not found'}
              </Typography>
            </CardTableDataCol>
          ),
          bidAmount: (
            <CardTableDataCol name="bidAmount">
              <Typography variant="subtitle2">{formatMoney(dollar_amount)}</Typography>
            </CardTableDataCol>
          ),
          status: (
            <CardTableDataCol name="status">
              <Typography variant="subtitle2">{status}</Typography>
            </CardTableDataCol>
          ),
          isNCFSigned: (
            <CardTableDataCol name="isNCFSigned">
              <Typography variant="subtitle2">
                {order?.entity?.new_client_form_signed ? <CheckIcon color="secondary" /> : <CloseIcon />}
              </Typography>
            </CardTableDataCol>
          ),
          isOrderDocsSigned: (
            <CardTableDataCol name="isOrderDocsSigned">
              <Typography variant="subtitle2">
                {order?.client_signed ? <CheckIcon color="secondary" /> : <CloseIcon />}
              </Typography>
            </CardTableDataCol>
          ),
          isWireInfoSent: (
            <CardTableDataCol name="isWireInfoSent">
              <Typography variant="subtitle2">
                {order?.wire_info_sent ? <CheckIcon color="secondary" /> : <CloseIcon />}
              </Typography>
            </CardTableDataCol>
          ),
          dateCreated: (
            <CardTableDataCol name="dateCreated">
              <Typography variant="subtitle2">{moment(order?.date_created).format('MM/DD/YYYY, h:mm A')}</Typography>
            </CardTableDataCol>
          ),
          source: (
            <CardTableDataCol name="source">
              <Typography variant="subtitle2">{getHumanReadableSource(opportunity?.source)}</Typography>
            </CardTableDataCol>
          ),
        }
        return (
          <CardTableRow
            key={id}
            id={id}
            columns={columns}
            highlightColor={
              opportunity?.source === 'crm_admin' ? theme.palette.primary.main : theme.palette.text.primary
            }
          />
        )
      })}
      {sortedBids.length === 0 && !loading && (
        <Card>
          <CardHeader title="None" />
        </Card>
      )}
      <LoadingRow isLoading={loading} />
    </CardTable>
  )
}
